import React from 'react';
import Map, { Source, Layer as MapLayer, Popup, type MapRef } from 'react-map-gl/mapbox';
import type { FilterSpecification } from 'mapbox-gl';
import { Loader2, Maximize2 } from 'lucide-react';
import 'mapbox-gl/dist/mapbox-gl.css';
import { apiClient } from '../../lib/apiClient';
import { heatRamp, GEO_MAP_HEIGHT } from './reportTheme';

// CTV-445: ZIP-boundary heatmap for the campaign report. Reuses the wizard's
// Mapbox setup (USMapWithRadius) but colors each delivered ZIP by impression
// volume (sequential green scale) and shows impressions on hover. Boundaries
// come from POST /geographic/zip-boundaries (the same census_zip_codes source
// the geotargeting map uses); impression counts are merged in client-side from
// the /submissions/:id/geo response.

interface ZipDatum {
  zip: string;
  impressions: number;
  completes: number;
}

interface ZipBoundary {
  zcta: string;
  latitude: number;
  longitude: number;
  boundary: GeoJSON.Geometry | null;
}

interface ZipHeatmapProps {
  byZip: ZipDatum[];
  accentColor?: string;
  // ZCTA to emphasize on the map (driven by hovering a row in the ZIP breakdown).
  highlightZip?: string | null;
  // Outline color for the highlighted ZIP. Pass the brand secondary/accent when
  // it's distinct enough from the primary; falls back to the primary accent.
  highlightColor?: string;
}

interface HoverInfo {
  lng: number;
  lat: number;
  zcta: string;
  impressions: number;
}

const MAP_HEIGHT = GEO_MAP_HEIGHT;
const PILL_IMAGE_ID = 'zip-label-pill';
// Manual zoom cap (what the user can scroll/pinch to). Auto-zoom (framing a ZIP
// hovered in the list) may push past this for small urban ZIPs that need a
// deeper zoom to fill the frame — we raise the cap just enough, on demand.
const DEFAULT_MAX_ZOOM = 11;
const AUTO_MAX_ZOOM = 16;

// A white box with a faint slate border, used as the ZIP label background (via
// the symbol layer's icon-text-fit) so labels read like the DMA map's pills and
// stay legible where a label overruns onto a lighter neighbor. All ZIP labels
// are 5 chars, so icon-text-fit stretches this near-uniformly.
function buildPillImage(): ImageData | null {
  if (typeof document === 'undefined') return null;
  const w = 24;
  const h = 14;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = 'rgba(203, 213, 225, 0.95)'; // slate-300
  ctx.lineWidth = 1;
  ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  return ctx.getImageData(0, 0, w, h);
}

// [minLng, minLat, maxLng, maxLat] of a polygon geometry, or null.
function geometryBbox(g: GeoJSON.Geometry): [number, number, number, number] | null {
  const bb: [number, number, number, number] = [180, 90, -180, -90];
  const visit = (c: unknown): void => {
    if (typeof (c as number[])[0] === 'number') {
      const [lng, lat] = c as [number, number];
      if (lng < bb[0]) bb[0] = lng;
      if (lat < bb[1]) bb[1] = lat;
      if (lng > bb[2]) bb[2] = lng;
      if (lat > bb[3]) bb[3] = lat;
      return;
    }
    for (const ch of c as unknown[]) visit(ch);
  };
  if (g.type === 'Polygon' || g.type === 'MultiPolygon') visit((g as GeoJSON.Polygon | GeoJSON.MultiPolygon).coordinates);
  else return null;
  return bb[0] > bb[2] ? null : bb;
}

const ZipHeatmap: React.FC<ZipHeatmapProps> = ({ byZip, accentColor, highlightZip, highlightColor }) => {
  const ramp = heatRamp(accentColor);
  const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN;
  const mapRef = React.useRef<MapRef>(null);
  const [boundaries, setBoundaries] = React.useState<ZipBoundary[] | null>(null);
  const [failed, setFailed] = React.useState(false);
  const [hover, setHover] = React.useState<HoverInfo | null>(null);
  // Current zoom cap; raised on demand when auto-zooming to a small ZIP.
  const [maxZoomCap, setMaxZoomCap] = React.useState(DEFAULT_MAX_ZOOM);

  const impByZip = React.useMemo(() => {
    const m: Record<string, number> = {};
    for (const z of byZip) m[z.zip] = z.impressions;
    return m;
  }, [byZip]);

  // Stable key so the fetch effect doesn't re-fire on every render.
  const zctaKey = React.useMemo(() => byZip.map((z) => z.zip).sort().join(','), [byZip]);

  React.useEffect(() => {
    const zctas = zctaKey ? zctaKey.split(',') : [];
    if (zctas.length === 0) {
      setBoundaries([]);
      return;
    }
    let cancelled = false;
    setFailed(false);
    apiClient
      .post<{ zips: ZipBoundary[] }>('/geographic/zip-boundaries', { zctas })
      .then((r) => {
        if (!cancelled) setBoundaries(r.zips ?? []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [zctaKey]);

  const features = React.useMemo(
    () =>
      (boundaries ?? [])
        .filter((b) => b.boundary)
        .map((b) => ({
          type: 'Feature' as const,
          geometry: b.boundary as GeoJSON.Geometry,
          properties: { zcta: b.zcta, impressions: impByZip[b.zcta] ?? 0 },
        })),
    [boundaries, impByZip],
  );

  // Stable so hover-driven re-renders don't re-set the Mapbox source.
  const geojson = React.useMemo<GeoJSON.FeatureCollection>(
    () => ({ type: 'FeatureCollection', features }),
    [features],
  );

  const maxImp = React.useMemo(
    () => Math.max(1, ...byZip.map((z) => z.impressions)),
    [byZip],
  );

  // Bounding box used to frame the map. Rather than fit every delivered ZIP —
  // which zooms out to include sparse, far-flung outliers and shrinks the core
  // ZIPs to specks — frame on the "core": the highest-volume ZIPs that together
  // make up ~70% of plays. That starts the view zoomed into the main delivery
  // area; the outliers are still drawn (just outside the initial frame) and
  // listed in the breakdown beside the map.
  const bbox = React.useMemo(() => {
    const pts = (boundaries ?? []).filter((b) => b.boundary);
    if (pts.length === 0) return null;
    const sorted = [...pts].sort((a, b) => (impByZip[b.zcta] ?? 0) - (impByZip[a.zcta] ?? 0));
    const total = sorted.reduce((s, b) => s + (impByZip[b.zcta] ?? 0), 0) || 1;
    const core: typeof sorted = [];
    let acc = 0;
    for (const b of sorted) {
      core.push(b);
      acc += impByZip[b.zcta] ?? 0;
      if (acc / total >= 0.7) break;
    }
    const lats = core.map((b) => b.latitude);
    const lngs = core.map((b) => b.longitude);
    return {
      minLat: Math.min(...lats),
      maxLat: Math.max(...lats),
      minLng: Math.min(...lngs),
      maxLng: Math.max(...lngs),
    };
  }, [boundaries, impByZip]);

  // New delivered footprint → reset the manual zoom cap (a prior small-ZIP
  // hover may have raised it).
  React.useEffect(() => {
    setMaxZoomCap(DEFAULT_MAX_ZOOM);
  }, [zctaKey]);

  // When a ZIP is highlighted from the list, zoom/pan to frame it — unless it's
  // already comfortably on screen (avoid jarring re-zoom between nearby rows).
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map || !highlightZip) return;
    const b = (boundaries ?? []).find((x) => x.zcta === highlightZip);
    if (!b) return;
    const bb = b.boundary ? geometryBbox(b.boundary) : null;
    const container = map.getContainer();
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    const corners: [number, number, number, number] = bb ?? [b.longitude, b.latitude, b.longitude, b.latitude];
    const p0 = map.project([corners[0], corners[1]]); // sw
    const p1 = map.project([corners[2], corners[3]]); // ne
    const cx = (p0.x + p1.x) / 2;
    const cy = (p0.y + p1.y) / 2;
    const frac = Math.max(Math.abs(p1.x - p0.x), Math.abs(p0.y - p1.y)) / Math.min(w, h);
    // Skip only if already roughly centered + well-sized, so the ZIP the hover
    // ends on always flies to center (not skipped just for being on screen).
    const centered = cx >= 0.3 * w && cx <= 0.7 * w && cy >= 0.3 * h && cy <= 0.7 * h;
    if (centered && frac >= 0.1 && frac <= 0.85) return;

    if (bb) {
      const bounds: [[number, number], [number, number]] = [
        [bb[0], bb[1]],
        [bb[2], bb[3]],
      ];
      // How deep we'd need to zoom to frame this ZIP. Small urban ZIPs exceed the
      // manual cap — raise the cap (up to AUTO_MAX_ZOOM) just for this so the
      // auto-zoom can actually reach them. setMaxZoom imperatively first so the
      // immediately-following fitBounds isn't clamped at the old cap.
      const cam = map.cameraForBounds(bounds, { padding: 60 });
      const needed = Math.min(AUTO_MAX_ZOOM, cam?.zoom ?? DEFAULT_MAX_ZOOM);
      if (needed > map.getMaxZoom()) {
        map.getMap().setMaxZoom(needed);
        setMaxZoomCap(needed);
      }
      map.fitBounds(bounds, { padding: 60, maxZoom: AUTO_MAX_ZOOM, duration: 550 });
    } else {
      const needed = Math.min(AUTO_MAX_ZOOM, 14);
      if (needed > map.getMaxZoom()) {
        map.getMap().setMaxZoom(needed);
        setMaxZoomCap(needed);
      }
      map.easeTo({ center: [b.longitude, b.latitude], zoom: needed, duration: 550 });
    }
  }, [highlightZip, boundaries]);

  // Without a token (or on a fetch error) we render nothing — the ZIP
  // breakdown table below still gives the full picture.
  if (!MAPBOX_TOKEN || failed) return null;

  // Loading: hold the map's footprint so the card doesn't jump.
  if (boundaries === null) {
    return (
      <div
        className="rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center text-[12px] text-slate-400"
        style={{ height: MAP_HEIGHT }}
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
        Loading map…
      </div>
    );
  }

  // No boundaries resolved (e.g. ZIPs unknown to census table) → table only.
  if (features.length === 0 || !bbox) return null;

  // Pad the centroid bbox ~40% on each side so the polygons aren't flush to the
  // edges and the view sits a level looser than a tight centroid fit.
  const latPad = Math.max((bbox.maxLat - bbox.minLat) * 0.4, 0.04);
  const lngPad = Math.max((bbox.maxLng - bbox.minLng) * 0.4, 0.04);
  const framed = {
    minLat: bbox.minLat - latPad,
    maxLat: bbox.maxLat + latPad,
    minLng: bbox.minLng - lngPad,
    maxLng: bbox.maxLng + lngPad,
  };
  // Frame on the core area up front (so the first paint is already there), then
  // fit precisely once the GL map is ready (onLoad).
  const span = Math.max(framed.maxLat - framed.minLat, framed.maxLng - framed.minLng);
  const initialZoom = span > 0 ? Math.max(5, Math.min(12, Math.log2(360 / (span * 2.2)))) : 9;
  const centerLng = (framed.minLng + framed.maxLng) / 2;
  const centerLat = (framed.minLat + framed.maxLat) / 2;

  // Re-frame to the default core view (same as onLoad), animated.
  const resetView = () => {
    mapRef.current?.fitBounds(
      [
        [framed.minLng, framed.minLat],
        [framed.maxLng, framed.maxLat],
      ],
      { padding: 32, maxZoom: 12, duration: 500 },
    );
  };

  // Data-driven fill: light → dark across the campaign's impression range, on the
  // brand-color heat ramp (or the default green).
  const fillColor = [
    'interpolate',
    ['linear'],
    ['get', 'impressions'],
    0,
    ramp.low,
    maxImp * 0.5,
    ramp.mid,
    maxImp,
    ramp.high,
  ] as unknown as string;

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden relative" style={{ height: MAP_HEIGHT }}>
      <Map
        // Remount when the delivered ZIP set changes so the initial view +
        // onLoad fitBounds re-frame to the new footprint (e.g. after adding
        // markets or filtering by date), rather than keeping the prior view.
        key={zctaKey}
        ref={mapRef}
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={{ longitude: centerLng, latitude: centerLat, zoom: initialZoom }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
        projection={{ name: 'mercator' }}
        attributionControl={false}
        interactiveLayerIds={['zip-heat-fill']}
        cursor={hover ? 'pointer' : 'default'}
        onMouseMove={(e) => {
          const f = e.features && e.features[0];
          if (f && f.properties) {
            setHover({
              lng: e.lngLat.lng,
              lat: e.lngLat.lat,
              zcta: String(f.properties.zcta),
              impressions: Number(f.properties.impressions) || 0,
            });
          } else {
            setHover(null);
          }
        }}
        onMouseLeave={() => setHover(null)}
        scrollZoom
        boxZoom={false}
        doubleClickZoom
        touchZoomRotate
        touchPitch={false}
        dragRotate={false}
        dragPan
        keyboard
        minZoom={3}
        maxZoom={maxZoomCap}
        // Keep panning within North America — no wandering off to other continents.
        maxBounds={[
          [-172, 15],
          [-52, 72],
        ]}
        onLoad={(e) => {
          const map = e.target;
          // Register the label-background pill before the symbol layer needs it.
          if (!map.hasImage(PILL_IMAGE_ID)) {
            const pill = buildPillImage();
            if (pill) map.addImage(PILL_IMAGE_ID, pill);
          }
          map.fitBounds(
            [
              [framed.minLng, framed.minLat],
              [framed.maxLng, framed.maxLat],
            ],
            { padding: 32, maxZoom: 12, duration: 0 },
          );
        }}
      >
        <Source id="zip-heat" type="geojson" data={geojson}>
          <MapLayer id="zip-heat-fill" type="fill" paint={{ 'fill-color': fillColor, 'fill-opacity': 0.82 }} />
          <MapLayer id="zip-heat-border" type="line" paint={{ 'line-color': '#ffffff', 'line-width': 1 }} />
          <MapLayer
            id="zip-heat-labels"
            type="symbol"
            layout={{
              'text-field': ['get', 'zcta'],
              'text-size': 10,
              'text-allow-overlap': false,
              // White pill behind the text (fitted to it), matching the DMA map.
              'icon-image': PILL_IMAGE_ID,
              'icon-text-fit': 'both',
              'icon-text-fit-padding': [2, 5, 2, 5],
              'icon-allow-overlap': false,
              // When labels collide, keep the higher-volume ZIPs.
              'symbol-sort-key': ['*', ['get', 'impressions'], -1] as unknown as number,
            }}
            paint={{ 'text-color': '#334155' }}
          />
          {/* Highlight the ZIP hovered in the breakdown list — a white casing
              under an accent outline so it reads on any fill shade. */}
          {highlightZip && (
            <MapLayer
              id="zip-heat-highlight-casing"
              type="line"
              filter={['==', ['get', 'zcta'], highlightZip] as unknown as FilterSpecification}
              paint={{ 'line-color': '#ffffff', 'line-width': 4 }}
            />
          )}
          {highlightZip && (
            <MapLayer
              id="zip-heat-highlight"
              type="line"
              filter={['==', ['get', 'zcta'], highlightZip] as unknown as FilterSpecification}
              paint={{ 'line-color': highlightColor || accentColor || ramp.high, 'line-width': 2.5 }}
            />
          )}
        </Source>
        {hover && (
          <Popup
            longitude={hover.lng}
            latitude={hover.lat}
            closeButton={false}
            closeOnClick={false}
            offset={12}
            anchor="bottom"
          >
            <div className="text-[11px] font-semibold text-slate-900">{hover.zcta}</div>
            <div className="text-[11px] text-slate-600">
              {hover.impressions.toLocaleString('en-US')} plays
            </div>
          </Popup>
        )}
      </Map>
      <button
        type="button"
        onClick={resetView}
        className="absolute top-2 right-2 z-10 bg-white/90 backdrop-blur-sm rounded-md px-2 py-1 shadow-sm border border-slate-200 text-[10px] font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1"
        title="Reset view"
      >
        <Maximize2 className="w-3 h-3" />
        Reset view
      </button>
      <div className="absolute bottom-2 left-2 bg-white/90 backdrop-blur-sm rounded-md px-2.5 py-1.5 shadow-sm border border-slate-200 flex items-center gap-2">
        <span className="text-[10px] text-slate-500">Fewer</span>
        <div className="h-2 w-20 rounded-full" style={{ background: `linear-gradient(to right, ${ramp.low}, ${ramp.high})` }} />
        <span className="text-[10px] text-slate-500">More plays</span>
      </div>
    </div>
  );
};

export default ZipHeatmap;
